/**
 * Single source of truth for DowUrk® organizational identifiers.
 *
 * NEVER inline these values anywhere in the codebase. Always import from here:
 *   import { ORG } from '@/config/org';
 *
 * Federal contracting officers, foundation program officers, and corporate
 * supplier-diversity reviewers verify these against SAM.gov + IRS records
 * before responding to outreach. Drift between pages is a disqualifier in
 * due diligence — that's why this file exists.
 *
 * Canonical phone number: (985) 351-8873 (per CEO directive Feb 2026).
 */
export const ORG = {
  legalName: 'DowUrk® Inc.',
  ein: '81-3555399',
  cageCode: '8JUP7',
  ueiCode: 'PNMVVH9EWSD3',
  irsStatus: '501(c)(3) Public Charity',
  rulingYear: 2023,
  founded: 2016,
  trademark: {
    mark: 'DowUrk®',
    regNumber: '6,557,997',
    registered: '2021-11-16',
    registry: 'USPTO Principal Register',
  },
  certifications: {
    candidSeal: 'Gold Transparency 2026',
    samStatus: 'Active',
    hudsonInitiative: 'Small Entrepreneurship (SE)',
    ledProgram: 'SEBD Certification Active',
    naicsPrimary: '813410',
  },
  address: {
    street: '45398 Shadowood Dr.',
    city: 'Hammond',
    state: 'LA',
    zip: '70401',
    country: 'US',
  },
  contact: {
    primaryPhone: '(985) 351-8873',
    primaryPhoneTel: '+19853518873',
    primaryEmail: 'info@dowurktoday.org',
    privacyEmail: 'privacy@dowurktoday.org',
  },
  social: {
    facebook: 'https://www.facebook.com/DowUrkinc',
    instagram: 'https://www.instagram.com/dowurkinc',
    twitter: 'https://www.twitter.com/dowurkinc',
    linkedin: 'https://www.linkedin.com/company/dowurk-inc/',
    youtube: 'https://www.youtube.com/channel/UCDFERb6RQLbxlErcPrUA94w',
  },
  candidProfileUrl: 'https://app.candid.org/profile/15332253/dowurk-inc-81-3555399/',
  // SAM.gov no longer exposes public per-entity profile URLs (deep links 404 as
  // of 2026). The Entity Information landing page is the canonical working URL
  // — federal/foundation reviewers paste the UEI into the search box there.
  samProfileUrl: 'https://sam.gov/content/entity-information',
  baseUrl: 'https://www.dowurktoday.org',
};

/**
 * Site-wide Organization JSON-LD schema (NGO type, per schema.org).
 * Inject once into the <head> via SEO component.
 */
export const ORG_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'NGO',
  name: 'DowUrk Inc.',
  alternateName: 'DowUrk® FramewUrk',
  url: ORG.baseUrl,
  logo: 'https://customer-assets.emergentagent.com/job_equity-focus/artifacts/875dlk3n_dowurk_color_shield.png',
  foundingDate: String(ORG.founded),
  taxID: ORG.ein,
  nonprofitStatus: 'Nonprofit501c3',
  address: {
    '@type': 'PostalAddress',
    streetAddress: ORG.address.street,
    addressLocality: ORG.address.city,
    addressRegion: ORG.address.state,
    postalCode: ORG.address.zip,
    addressCountry: ORG.address.country,
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: ORG.contact.primaryPhoneTel,
    contactType: 'customer service',
    email: ORG.contact.primaryEmail,
  },
  sameAs: [
    ORG.social.facebook,
    ORG.social.instagram,
    ORG.social.linkedin,
    ORG.social.youtube,
    ORG.candidProfileUrl,
  ],
};
